import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, ShieldCheckIcon, TriangleAlertIcon, BellIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
// FR lexicon (locked) for the Smart Water / Compteurs intelligents page. EN untouched.
const FR: Record<string, string> = {
  // Comparison rows
  'Manual Quarterly Readings': 'Relevés manuels trimestriels',
  'Real-Time or Photo Capture': 'Temps réel ou saisie photo',
  'Leak Found on Bill': 'Fuite découverte sur la facture',
  'Anomaly Detected Early': 'Anomalie détectée tôt',
  'Anonymous Meter': 'Compteur anonyme',
  'Verified Owner': 'Propriétaire vérifié',
  'Reactive Operations': 'Opérations réactives',
  'Proactive Intervention': 'Intervention proactive',
  // Detection→Action flow
  'Property & Meter': 'Propriété et compteur',
  'Consumption Data': 'Données de consommation',
  'ATNA Detection': 'Détection ATNA',
  'Notification & Action': 'Notification et action',
  Resolution: 'Résolution',
  // Compliance badges + proof attributes
  'Law 25 Ready': 'Conforme Loi 25',
  'Canadian Hosted': 'Hébergé au Canada',
  'Auditable Actions': 'Actions traçables',
  'Live Production Smart Water Deployment': 'Déploiement Compteurs intelligents en production active',
  'Verified Identity Infrastructure': "Infrastructure d'identité vérifiée",
  'Operational Monitoring': 'Surveillance opérationnelle',
  // Benefits — Property Owner
  'Early leak detection': 'Détection précoce des fuites',
  'Lower water costs': "Coûts d'eau réduits",
  'Property-level insights': 'Visibilité par propriété',
  'Personalized recommendations': 'Recommandations personnalisées',
  'Multiple property management': 'Gestion de plusieurs propriétés',
  // Benefits — City / Utility
  'Reduced disputes': 'Litiges réduits',
  'Conservation goals': 'Objectifs de conservation',
  'Automated workflows': 'Flux de travail automatisés',
  'Fewer manual readings': 'Moins de relevés manuels',
  'Better operational visibility': 'Meilleure visibilité opérationnelle',
  // Beyond water resources
  Water: 'Eau',
  Gas: 'Gaz',
  Electricity: 'Électricité',
  'Environmental Monitoring': 'Surveillance environnementale',
  Utilities: 'Services publics',
  'Smart Infrastructure': 'Infrastructure intelligente'
};
// Research cards (label + description) for "Built on Real Infrastructure".
const RESEARCH_FR: Record<string, {
  label: string;
  desc: string;
}> = {
  'Leak Reduction Potential': {
    label: 'Potentiel de réduction des fuites',
    desc: 'Des notifications ciblées de compteurs intelligents ont permis de réduire les fuites en aval du compteur jusqu\u2019à 89\u00A0%.*'
  },
  'Faster Repair Response': {
    label: 'Réparation plus rapide',
    desc: 'La visibilité en ligne de la consommation a permis de réduire les délais de réparation des fuites jusqu\u2019à 34\u00A0%.*'
  },
  'Water Consumption Reduction': {
    label: "Réduction de la consommation d'eau",
    desc: "La recherche montre que la visibilité en temps réel de la consommation peut réduire l'usage résidentiel de l'eau.*"
  }
};
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  trad: 'Manual Quarterly Readings',
  bciti: 'Real-Time or Photo Capture'
}, {
  trad: 'Leak Found on Bill',
  bciti: 'Anomaly Detected Early'
}, {
  trad: 'Anonymous Meter',
  bciti: 'Verified Owner'
}, {
  trad: 'Reactive Operations',
  bciti: 'Proactive Intervention'
}];
const ACTION_FLOW = [{
  label: 'Verified Owner'
}, {
  label: 'Property & Meter'
}, {
  label: 'Consumption Data'
}, {
  label: 'ATNA Detection',
  highlight: true
}, {
  label: 'Notification & Action',
  highlight: true
}, {
  label: 'Resolution'
}];
const PROOF_ATTRIBUTES = ['Live Production Smart Water Deployment', 'Verified Identity Infrastructure', 'Operational Monitoring', 'Law 25 Ready', 'Canadian Hosted', 'Auditable Actions'];
const BEYOND_RESOURCES = ['Water', 'Gas', 'Electricity', 'Environmental Monitoring', 'Utilities', 'Smart Infrastructure'];
export function SmartWater() {
  const {
    lang
  } = useLanguage();
  const t = (en: string) => lang === 'FR' ? FR[en] ?? en : en;
  const researchLabel = (en: string) => lang === 'FR' ? RESEARCH_FR[en]?.label ?? en : en;
  const researchDesc = (en: string, desc: string) => lang === 'FR' ? RESEARCH_FR[en]?.desc ?? desc : desc;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Smart Water'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-2608">
      <Header data-id="element-2609" />

      <main className="pt-[120px]" data-id="element-2610">
        {/* SECTION 1: HERO — outcome, not process */}
        <section className="pt-24 pb-32 px-6" data-id="element-2611">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2612">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-2613">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-indigo uppercase mb-6" data-id="element-2614">
                {lang === 'FR' ? 'BCITI Compteurs intelligents' : 'BCITI Smart Water'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-2615">
                {lang === 'FR' ? "Détectez la fuite. Notifiez le propriétaire. Passez à l'action." : 'Detect the leak. Notify the owner. Take action.'}
              </h1>
              <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-6" data-id="element-2616">
                {lang === 'FR' ? "Chaque compteur d'eau est rattaché à une propriété vérifiée et à un propriétaire vérifié." : 'Every water meter is connected to a verified property and a verified owner.'}
              </p>
              <p className="font-sans text-lg text-[#121212]/60 leading-relaxed mb-10" data-id="element-2617">
                {lang === 'FR' ? 'ATNA détecte les anomalies, alerte les bonnes personnes et peut déclencher des actions sur des flux de travail autorisés.' : 'ATNA detects anomalies, alerts the right people, and can initiate actions on authorized workflows.'}
              </p>
              <div className="flex flex-wrap items-center gap-4" data-id="element-2618">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Smart Water Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-2619">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2620" />
                </Link>
                <a href="#casselman-proof" onClick={e => {
                e.preventDefault();
                document.getElementById('casselman-proof')?.scrollIntoView({
                  behavior: 'smooth'
                });
                track('Proof CTA Clicked', {
                  location: 'Smart Water Hero'
                });
              }} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-lg hover:border-indigo hover:text-indigo transition-colors" data-id="element-2621">
                  {lang === 'FR' ? 'Voir en production' : 'See It In Production'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2622" />
                </a>
              </div>
              <p className="font-sans text-sm text-[#121212]/45 mt-5" data-id="element-2623">
                {lang === 'FR' ? 'Déploiement actif à Casselman · Hébergé au Canada · Conforme Loi 25' : 'Live deployment in Casselman · Canadian Hosted · Law 25 Ready'}
              </p>
            </motion.div>

            {/* Product visual — consumption dashboard + leak alert + notification */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex justify-center" data-id="element-2624">
              <img src={lang === 'FR' ? 'https://cdn.magicpatterns.com/uploads/1htYDEx1nwFBAjD6eqkU2q/ChatGPT_Image_7_juil._2026,_15_20_34.png' : 'https://cdn.magicpatterns.com/uploads/kexd9nWercQpt16QhxL96z/ChatGPT_Image_7_juil._2026,_15_06_01.png'} alt={lang === 'FR' ? 'Tableau de bord Compteurs intelligents : consommation quotidienne 1 840 L/jour, anomalie détectée — fuite possible, propriétaire notifié (Marie Tremblay, propriétaire vérifié). Vue illustrative du produit.' : 'Smart Meters dashboard: daily consumption 1,840 L/day, anomaly detected — possible leak, owner notified (Marie Tremblay, verified owner). Illustrative product view.'} className="w-full max-w-sm h-auto object-contain mx-auto" data-id="element-2625" />
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: THE COST OF DISCOVERING TOO LATE */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2626">
          <div className="max-w-4xl mx-auto" data-id="element-2627">
            <div className="text-center mb-20" data-id="element-2628">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6" data-id="element-2629">
                {lang === 'FR' ? "La plupart des problèmes d'eau sont découverts une fois les dégâts faits." : 'Most water issues are discovered after the damage is done.'}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-xl text-[#121212]/60 max-w-2xl mx-auto" data-id="element-2630">
                {lang === 'FR' ? "Les processus traditionnels détectent les problèmes au moment de la facturation. L'intelligence opérationnelle les détecte au moment où ils surviennent." : 'Traditional processes identify problems at billing time. Operational intelligence identifies problems when they happen.'}
              </motion.p>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="bg-white border border-[#E4E6EC] overflow-hidden mb-8" data-id="element-2631">
              <div className="grid grid-cols-2 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2632">
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase border-r border-[#E4E6EC]" data-id="element-2633">
                  {lang === 'FR' ? 'Approche traditionnelle' : 'Traditional Approach'}
                </div>
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-indigo uppercase" data-id="element-2634">
                  {lang === 'FR' ? 'BCITI Compteurs intelligents' : 'BCITI Smart Water'}
                </div>
              </div>
              {COMPARISON_ROWS.map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-[#E4E6EC] last:border-0 hover:bg-[#FAFAFB]/50 transition-colors" data-id="element-2635">
                  <div className="p-6 font-serif text-lg text-[#121212]/50 border-r border-[#E4E6EC]" data-id="element-2636">
                    {t(row.trad)}
                  </div>
                  <div className="p-6 font-serif text-lg text-[#121212]" data-id="element-2637">
                    {t(row.bciti)}
                  </div>
                </div>)}
            </motion.div>

            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center font-sans text-sm text-[#121212]/50 tracking-wide" data-id="element-2638">
              {lang === 'FR' ? 'Chaque compteur est rattaché à une relation de propriété vérifiée.' : 'Every meter is connected to a verified property relationship.'}
            </motion.p>
          </div>
        </section>

        {/* SECTION 3: IDENTITY CREATES CONTEXT — text-only transition */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2639">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2640">
            <motion.span {...reveal} className="block font-sans text-xs font-semibold tracking-[0.25em] text-indigo uppercase mb-8" data-id="element-2641">
              {lang === 'FR' ? "L'identité crée le contexte" : 'Identity Creates Context'}
            </motion.span>

            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.05
          }} className="font-serif text-2xl text-[#121212] max-w-2xl mx-auto mb-8 leading-snug" data-id="element-2642">
              {lang === 'FR' ? "Tout le monde mesure l'eau. La nôtre sait à qui appartient la fuite — et agit avant la facture." : 'Everyone meters water. Ours knows whose leak it is — and acts before the bill.'}
            </motion.p>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="max-w-2xl mx-auto space-y-6" data-id="element-2643">
              <p className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-2644">
                {lang === 'FR' ? 'Un propriétaire vérifié est rattaché à une propriété vérifiée. Une propriété vérifiée est rattachée à un compteur vérifié.' : 'A verified owner is connected to a verified property. A verified property is connected to a verified meter.'}
              </p>
              <p className="font-sans text-xl text-[#121212]/60 pt-2" data-id="element-2645">
                {lang === 'FR' ? 'Ce contexte permet des actions intelligentes.' : 'That context enables intelligent actions.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4: FROM DETECTION TO ACTION — the only workflow */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2646">
          <div className="max-w-4xl mx-auto" data-id="element-2647">
            <div className="text-center mb-6" data-id="element-2648">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6" data-id="element-2649">
                {lang === 'FR' ? "De la détection à l'action." : 'From detection to action.'}
              </motion.h2>
              <motion.p {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="font-sans text-xl text-[#121212]/60" data-id="element-2650">
                {lang === 'FR' ? "La plupart des systèmes s'arrêtent à l'alerte. ATNA poursuit jusqu'à l'action." : 'Most systems stop at alerts. ATNA continues to action.'}
              </motion.p>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} className="flex justify-center mb-16" data-id="element-2651">
              <span className="inline-flex items-center gap-2 font-sans text-xs font-semibold tracking-[0.18em] uppercase text-indigo border border-indigo/30 px-4 py-2" data-id="element-2652">
                <span className="inline-block w-1.5 h-1.5 bg-indigo" aria-hidden="true" data-id="element-2653" />
                {lang === 'FR' ? 'Propulsé par ATNA' : 'Powered by ATNA'}
              </span>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex flex-col items-center mb-16" data-id="element-2654">
              {ACTION_FLOW.map((node, i, arr) => <Fragment key={node.label} data-id="element-2655">
                  <div className={`w-full max-w-md py-4 px-6 text-center border font-sans text-sm ${node.highlight ? 'border-indigo bg-indigo/5 text-indigo font-medium' : 'border-[#E4E6EC] bg-white text-[#121212]'}`} data-id="element-2656">
                    {t(node.label)}
                  </div>
                  {i < arr.length - 1 && <ArrowDownIcon className="w-4 h-4 text-[#E4E6EC] my-2" data-id="element-2657" />}
                </Fragment>)}
            </motion.div>

            {/* Compliance callout */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="border border-indigo/20 bg-indigo/[0.03] p-8 max-w-2xl mx-auto" data-id="element-2658">
              <div className="flex items-start gap-4" data-id="element-2659">
                <ShieldCheckIcon className="w-5 h-5 text-indigo shrink-0 mt-0.5" data-id="element-2660" />
                <div data-id="element-2661">
                  <p className="font-sans text-sm text-[#121212]/70 leading-relaxed mb-5" data-id="element-2662">
                    {lang === 'FR' ? "Les actions nécessitant une autorisation financière ou opérationnelle ne sont exécutées qu'avec un consentement explicite et une traçabilité complète." : 'Actions requiring financial or operational authorization are executed only with explicit consent and full auditability.'}
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-2663">
                    {['Law 25 Ready', 'Canadian Hosted', 'Auditable Actions'].map(badge => <span key={badge} className="font-sans text-xs font-medium text-indigo border border-indigo/30 px-3 py-1.5" data-id="element-2664">
                        {t(badge)}
                      </span>)}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 5: RESULTS FOR EVERY STAKEHOLDER */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-2665">
          <div className="max-w-5xl mx-auto" data-id="element-2666">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-20" data-id="element-2667">
              {lang === 'FR' ? 'Avantages pour les propriétaires et les organisations.' : 'Benefits for owners and organizations.'}
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-2668">
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="border border-[#E4E6EC] p-10" data-id="element-2669">
                <div className="font-sans text-sm font-semibold tracking-widest text-[#121212]/40 uppercase mb-8" data-id="element-2670">
                  {lang === 'FR' ? 'Propriétaire' : 'Property Owner'}
                </div>
                <ul className="space-y-5" data-id="element-2671">
                  {['Early leak detection', 'Lower water costs', 'Property-level insights', 'Personalized recommendations', 'Multiple property management'].map(item => <li key={item} className="font-sans text-lg text-[#121212]/70 flex items-start gap-3" data-id="element-2672">
                      <span className="text-indigo mt-1.5 text-xs" data-id="element-2673">•</span>
                      {t(item)}
                    </li>)}
                </ul>
              </motion.div>

              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.2
            }} className="border border-[#E4E6EC] p-10" data-id="element-2674">
                <div className="font-sans text-sm font-semibold tracking-widest text-[#121212]/40 uppercase mb-8" data-id="element-2675">
                  {lang === 'FR' ? 'Ville / Service public' : 'City / Utility'}
                </div>
                <ul className="space-y-5" data-id="element-2676">
                  {['Reduced disputes', 'Conservation goals', 'Automated workflows', 'Fewer manual readings', 'Better operational visibility'].map(item => <li key={item} className="font-sans text-lg text-[#121212]/70 flex items-start gap-3" data-id="element-2677">
                      <span className="text-indigo mt-1.5 text-xs" data-id="element-2678">•</span>
                      {t(item)}
                    </li>)}
                </ul>
              </motion.div>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="max-w-2xl mx-auto text-center" data-id="element-2679">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" aria-hidden="true" data-id="element-2680" />
              <p className="font-serif text-2xl text-[#121212]" data-id="element-2681">
                {lang === 'FR' ? 'La visibilité crée la prise de conscience.' : 'Visibility creates awareness.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]/50" data-id="element-2682">
                {lang === 'FR' ? 'La prise de conscience favorise la conservation.' : 'Awareness drives conservation.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 6: PROVEN IN PRODUCTION — Casselman */}
        <section id="casselman-proof" className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB] scroll-mt-24" data-id="element-2683">
          <div className="max-w-5xl mx-auto" data-id="element-2684">
            <h2 className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-2685">
              {lang === 'FR' ? 'Bâti sur une infrastructure réelle' : 'Built on Real Infrastructure'}
            </h2>

            {/* Market-context research kicker — external research findings, not Casselman outcomes */}
            <div className="font-sans text-xs font-semibold tracking-[0.18em] text-indigo uppercase mb-5" data-id="element-2686">
              {lang === 'FR' ? 'Contexte de marché — Recherche évaluée par les pairs' : 'Market Context — Peer-Reviewed Research'}
            </div>

            {/* Research-supported proof grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-6" data-id="element-2687">
              {[{
              stat: '89%',
              label: 'Leak Reduction Potential',
              desc: 'Targeted smart meter notifications have been shown to reduce post-meter leaks by up to 89%.*'
            }, {
              stat: '34%',
              label: 'Faster Repair Response',
              desc: 'Online consumption visibility has been shown to reduce leak repair times by up to 34%.*'
            }, {
              stat: '5–15%',
              label: 'Water Consumption Reduction',
              desc: 'Research shows that real-time consumption visibility can reduce residential water usage.*'
            }].map(card => <div key={card.label} className="bg-white p-10" data-id="element-2688">
                  <div className="font-serif text-5xl text-[#121212] mb-3" data-id="element-2689">
                    {card.stat}
                  </div>
                  <div className="font-sans text-sm font-semibold tracking-wide text-indigo uppercase mb-4" data-id="element-2690">
                    {researchLabel(card.label)}
                  </div>
                  <p className="font-sans text-sm text-[#121212]/60 leading-relaxed" data-id="element-2691">
                    {researchDesc(card.label, card.desc)}
                  </p>
                </div>)}
            </div>

            <p className="font-sans text-xs text-[#121212]/45 leading-relaxed max-w-3xl mb-16" data-id="element-2692">
              {lang === 'FR' ? "*Résultats issus d'études évaluées par les pairs sur les compteurs intelligents et la rétroaction de consommation. Les résultats varient selon le déploiement, l'infrastructure et la participation des utilisateurs." : '*Research findings from peer-reviewed smart water and consumption feedback studies. Results vary by deployment, infrastructure and user participation.'}
            </p>

            {/* Hairline divider — separates market-context research from the live Casselman deployment */}
            <div className="w-full h-px bg-[#E4E6EC] mb-16" aria-hidden="true" data-id="element-2693" />

            {/* Infrastructure proof panel — Casselman deployment facts only, no claimed performance figures */}
            <div className="bg-white border border-indigo/20 p-12 relative shadow-sm" data-id="element-2694">
              <div className="absolute -top-4 left-12 bg-indigo text-white font-sans text-xs font-bold tracking-widest uppercase px-4 py-2" data-id="element-2695">
                {lang === 'FR' ? 'Environnement de production actif' : 'Live Production Environment'}
              </div>

              <div className="mb-12" data-id="element-2696">
                <h3 className="font-serif text-3xl text-[#121212] mb-2" data-id="element-2697">
                  {lang === 'FR' ? 'Municipalité de Casselman' : 'Town of Casselman'}
                </h3>
                <p className="font-sans text-[#121212]/60" data-id="element-2698">
                  {lang === 'FR' ? "Bâtie sur l'infrastructure BCITI, raffinée par des années d'usage en production." : 'Built on BCITI infrastructure refined through years of production use.'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-12 mb-12" data-id="element-2699">
                {PROOF_ATTRIBUTES.map(item => <div key={item} className="flex items-center gap-3" data-id="element-2700">
                    <ShieldCheckIcon className="w-4 h-4 text-indigo shrink-0" data-id="element-2701" />
                    <span className="font-sans text-base text-[#121212]" data-id="element-2702">
                      {t(item)}
                    </span>
                  </div>)}
              </div>

              {/* Explore Casselman CTA */}
            </div>

            {/* Confidence-peak CTA — glued directly to the Casselman proof */}
            <div className="text-center mt-16" data-id="element-2703">
              <p className="font-serif text-2xl text-[#121212] mb-6" data-id="element-2704">
                {lang === 'FR' ? 'Voyez-la fonctionner en production.' : 'See it running in production.'}
              </p>
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Smart Water Casselman Proof'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-2705">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2706" />
              </Link>
            </div>
          </div>
        </section>

        {/* SECTION 7: BEYOND WATER — discreet product-expansion band */}
        <section className="py-16 px-6 border-t border-[#E4E6EC]" data-id="element-2707">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2708">
            <motion.h2 {...reveal} className="font-serif text-2xl text-[#121212] mb-6" data-id="element-2709">
              {lang === 'FR' ? "L'eau aujourd'hui. Toute ressource mesurée demain." : 'Water today. Any measured resource tomorrow.'}
            </motion.h2>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="flex flex-wrap justify-center gap-3 mb-6" data-id="element-2710">
              {BEYOND_RESOURCES.map(res => <div key={res} className="px-4 py-1.5 border border-[#E4E6EC] bg-white font-sans text-sm text-[#121212]/70" data-id="element-2711">
                  {t(res)}
                </div>)}
            </motion.div>

            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="font-sans text-base text-[#121212]/60 max-w-2xl mx-auto" data-id="element-2712">
              {lang === 'FR' ? 'Une fois les relations vérifiées établies, chaque ressource mesurée devient exploitable.' : 'Once verified relationships exist, every measured resource becomes actionable.'}
            </motion.p>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-2713">
          <div className="max-w-3xl mx-auto text-center" data-id="element-2714">
            <motion.h2 {...reveal} className="font-serif text-5xl text-[#121212] mb-8 leading-tight" data-id="element-2715">
              {lang === 'FR' ? 'Détectez plus tôt. Réagissez plus vite. Agissez intelligemment.' : 'Detect earlier. Respond faster. Act intelligently.'}
            </motion.h2>
            <motion.p {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-sans text-xl text-[#121212]/60 mb-12" data-id="element-2716">
              {lang === 'FR' ? 'Identité vérifiée. Propriétés vérifiées. Actions vérifiées.' : 'Verified identity. Verified properties. Verified actions.'}
            </motion.p>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-2717">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Smart Water Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-2718">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-2719" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2720" />
    </div>;
}